/**
 * The Test tab: a REAL run of the draft against a past event, with sends
 * rendered-and-held (platform test mode). Reads and AI show their real
 * results; every send shows exactly what would have gone out, marked
 * "Previewed — not sent". No legend — each state explains itself in place.
 */
import { useState } from 'react';
import { Button, Card, Select } from '@servicecompressionllc/sc-design-system';
import type { EventRunStep, PipelineEvent } from '../../lib/types.js';
import type { useBuilderEditorLogic } from './useBuilderLogic.js';
import { humanizeKey } from './definitionEdit.js';

const STEP_VERB: Record<string, string> = {
  filter: 'only if',
  branch: 'split',
  notify: 'notify',
  approval: 'ask a person',
};

const formatEventLabel = (e: PipelineEvent): string =>
  `${e.type} · ${new Date(e.createdAt).toLocaleString()}`;

const Dot = ({ status }: { status: EventRunStep['status'] }) => (
  <span
    className={`mt-1 h-[15px] w-[15px] flex-none rounded-full border-2 ${
      status === 'done'
        ? 'border-success bg-success'
        : status === 'previewed'
          ? 'border-warning'
          : status === 'failed'
            ? 'border-danger bg-danger'
            : 'border-outline-strong'
    }`}
    aria-hidden
  />
);

const StatusBadge = ({ status }: { status: EventRunStep['status'] }) => {
  const meta =
    status === 'done'
      ? { text: 'Ran', cls: 'text-success bg-success/10' }
      : status === 'previewed'
        ? { text: 'Previewed — not sent', cls: 'text-warning bg-warning/10' }
        : status === 'failed'
          ? { text: 'Failed', cls: 'text-danger bg-danger/10' }
          : status === 'active'
            ? { text: 'Running…', cls: 'text-info bg-info/10' }
            : null;
  if (!meta) return null;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide ${meta.cls}`}
    >
      {meta.text}
    </span>
  );
};

/** A previewed send: exactly what would have gone out, held at the door. */
const SendPreview = ({ data }: { data: Record<string, unknown> }) => {
  const to = Array.isArray(data['to']) ? (data['to'] as string[]).join(', ') : null;
  const title = typeof data['title'] === 'string' ? data['title'] : null;
  const body = typeof data['body'] === 'string' ? data['body'] : null;
  const eventType = typeof data['eventType'] === 'string' ? data['eventType'] : null;
  const rows = Object.entries(
    (data['payload'] ?? data['input'] ?? {}) as Record<string, unknown>,
  ).filter(([, v]) => v !== undefined);
  return (
    <div className="mt-2 max-w-xl overflow-hidden rounded-lg border border-warning/60 bg-surface">
      <div className="border-b border-outline px-3 py-1.5 text-xs text-ink-subtle">
        Would go to:{' '}
        <span className="font-semibold text-ink-muted">{to || 'no destination chosen yet'}</span>
      </div>
      <div className="px-3 py-2">
        {eventType && (
          <div className="text-xs text-ink-muted">
            event <code className="rounded bg-surface-elevated px-1">{eventType}</code>
          </div>
        )}
        {title && <div className="text-sm font-semibold">{title}</div>}
        {body && <div className="mt-0.5 whitespace-pre-wrap text-sm text-ink-muted">{body}</div>}
        {rows.length > 0 && (
          <dl className="mt-1 grid gap-0.5 text-xs">
            {rows.map(([k, v]) => (
              <div key={k} className="flex gap-2">
                <dt className="text-ink-subtle">{humanizeKey(k)}</dt>
                <dd className="min-w-0 break-words text-ink-muted">{String(v)}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </div>
  );
};

/** Real outputs of a read/AI step, labeled the way the builder labels them. */
const OutputsTable = ({ data }: { data: Record<string, unknown> }) => {
  const [showReport, setShowReport] = useState(false);
  const entries = Object.entries(data).filter(([k]) => k !== 'report');
  const report = typeof data['report'] === 'string' ? data['report'] : null;
  if (entries.length === 0 && !report) return null;
  return (
    <div className="mt-2 overflow-hidden rounded-lg border border-outline bg-surface text-sm">
      {entries.map(([k, v]) => (
        <div key={k} className="flex gap-3 border-b border-outline px-3 py-1.5 last:border-b-0">
          <span className="w-52 flex-none text-xs leading-5 text-ink-muted">{humanizeKey(k)}</span>
          <span className="min-w-0 whitespace-pre-wrap break-words text-xs leading-5">
            {String(v)}
          </span>
        </div>
      ))}
      {report && (
        <div className="border-t border-outline px-3 py-1.5">
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => setShowReport((s) => !s)}
          >
            {showReport ? 'Hide the full report' : 'Show the full report ▸'}
          </button>
          {showReport && (
            <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-[0.72rem] leading-5 text-ink-muted">
              {report}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

const StepRow = ({ step }: { step: EventRunStep }) => {
  const data =
    step.data !== null && typeof step.data === 'object' && !Array.isArray(step.data)
      ? (step.data as Record<string, unknown>)
      : null;
  const isPreview = step.status === 'previewed';
  return (
    <li className="relative flex gap-3 pb-5 last:pb-0">
      <Dot status={step.status} />
      <div className={`min-w-0 flex-1 ${step.status === 'pending' ? 'opacity-50' : ''}`}>
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-[0.65rem] font-bold uppercase tracking-wider text-ink-subtle">
            {STEP_VERB[step.type] ?? step.type}
          </span>
          <span className="text-sm font-semibold">{step.name ?? ''}</span>
          <StatusBadge status={step.status} />
        </div>
        {/* A string result is the step's own sentence (a stop reason, an
            error); an object renders as a preview or an outputs table. */}
        {typeof step.data === 'string' && (
          <p
            className={`mt-1 max-w-xl text-xs ${step.status === 'failed' ? 'text-danger' : 'text-ink-muted'}`}
          >
            {step.data}
          </p>
        )}
        {data && isPreview && <SendPreview data={data} />}
        {data && !isPreview && <OutputsTable data={data} />}
      </div>
    </li>
  );
};

export const TestTab = ({ logic }: { logic: ReturnType<typeof useBuilderEditorLogic> }) => {
  const run = logic.testRun;
  const ranSteps = run?.steps.filter((s) => s.status !== 'pending') ?? [];
  return (
    <div className="grid max-w-3xl gap-4">
      <Card padding="md" className="min-w-0">
        <h2 className="mb-1 text-sm font-semibold">
          {logic.isScheduled
            ? 'Rehearse this workflow as if the schedule fired'
            : 'Rehearse this workflow against a real event'}
        </h2>
        <p className="mb-4 max-w-2xl text-xs text-ink-muted">
          A test runs this draft for real — lookups fetch real data and AI writes its real answers —
          but <strong>nothing leaves the platform</strong>: messages and deliveries are rendered so
          you can read exactly what would go out, then held.
        </p>
        {logic.isScheduled ? (
          /* The clock is the trigger — there may never have been a first
             occurrence, so a scheduled test is simply "run it now". */
          <Button
            variant="secondary"
            onClick={logic.runTest}
            disabled={!logic.canTest || logic.testRunning}
          >
            {logic.testRunning ? 'Running…' : 'Run now'}
          </Button>
        ) : logic.recentEvents.length === 0 ? (
          <p className="text-sm text-ink-muted">
            Nothing has arrived on this trigger yet — once the first event comes in, you can replay
            it here.
          </p>
        ) : (
          <div className="flex min-w-0 flex-wrap items-end gap-2">
            <label className="grid min-w-0 flex-1 basis-80 gap-1 text-xs text-ink-muted">
              Which event to replay
              <Select
                aria-label="Test event"
                searchable={false}
                options={logic.recentEvents.map((e) => ({
                  value: e.id,
                  label: formatEventLabel(e),
                }))}
                value={logic.selectedEventId ? [logic.selectedEventId] : []}
                onChange={(v) => logic.setTestEventId(v[0] ?? '')}
              />
            </label>
            <Button
              variant="secondary"
              onClick={logic.runTest}
              disabled={!logic.canTest || logic.testRunning}
            >
              {logic.testRunning ? 'Running…' : 'Run test'}
            </Button>
          </div>
        )}
        {logic.testError && (
          <p className="mt-2 text-xs text-danger">
            The test couldn’t start: {logic.testError.message}
          </p>
        )}
      </Card>

      {run && (
        <Card padding="md" className="min-w-0">
          <div className="mb-4 flex flex-wrap items-baseline gap-2">
            <span className="text-[0.7rem] font-bold uppercase tracking-wider text-ink-subtle">
              Test run
            </span>
            <span className="text-xs text-ink-subtle">
              {run.status === 'running'
                ? 'running…'
                : run.status === 'failed'
                  ? 'a step failed — details below'
                  : `${String(ranSteps.length)} step${ranSteps.length === 1 ? '' : 's'} · nothing was sent`}
            </span>
          </div>
          <ul className="grid" aria-label="Test run steps">
            {run.steps.map((s) => (
              <StepRow key={s.path} step={s} />
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};
